import { extendSchema } from "graphql";
import { DateTime } from "luxon";

function startOfWeek(date: string) {
  return DateTime.fromISO(date).startOf("week");
}
function normalizeEndAt(isAllDayEvent: boolean, endAt: string) {
  return isAllDayEvent
    ? (DateTime.fromISO(endAt).plus({ days: 1 }).toISO() ?? endAt)
    : endAt;
}
type Node = Readonly<{
  id: string;
  endAt: { isoString: string };
  startAt: { isoString: string };
  isAllDayEvent: boolean;
  createdAt: { isoString: string };
}>;

export function createCalendarOverviewListData<T extends Node>(
  events: readonly { node: T }[]
) {
  const sortedEvents = events.slice().sort((a, b) => {
    const startA = a.node.startAt.isoString;
    const startB = b.node.startAt.isoString;
    if (startA !== startB) {
      return startA < startB ? -1 : 1;
    }

    const endA = a.node.endAt.isoString;
    const endB = b.node.endAt.isoString;
    if (endA !== endB) {
      return endA < endB ? 1 : -1;
    }

    if (a.node.isAllDayEvent !== b.node.isAllDayEvent) {
      return a.node.isAllDayEvent ? -1 : 1;
    }

    if (a.node.createdAt.isoString !== b.node.createdAt.isoString) {
      return a.node.createdAt.isoString < b.node.createdAt.isoString ? -1 : 1;
    }

    if (a.node.id !== b.node.id) {
      return a.node.id > b.node.id ? -1 : 1;
    }

    return 0;
  });
  const now = DateTime.now().toUTC().toISO();

  const weeks = new Map<
    string,
    (T & { itemType: "event"; isActive: boolean })[]
  >();

  for (const { node } of sortedEvents) {
    const endAt = normalizeEndAt(node.isAllDayEvent, node.endAt.isoString);
    const isActive = node.startAt.isoString <= now && endAt >= now;
    const startAtWeek = startOfWeek(
      node.startAt.isoString < now ? now : node.startAt.isoString
    );
    if (!startAtWeek) {
      continue;
    }
    const weekKey = startAtWeek.toISO();
    const event = {
      ...node,
      isActive,
      itemType: "event" as const,
    };
    if (weekKey) {
      if (weeks.has(weekKey)) {
        weeks.get(weekKey)?.push(event);
      } else {
        weeks.set(weekKey, [event, ...(weeks.get(weekKey) ?? [])]);
      }
    }
  }

  return [...weeks.entries()].flatMap(([weekKey, eventsInWeek]) => {
    return eventsInWeek.length > 0
      ? [
          {
            itemType: "week" as const,
            date: DateTime.fromISO(weekKey),
            numberOfEvents: eventsInWeek.length,
          },
          ...eventsInWeek,
        ]
      : [];
  });
}
